import { buildLandingGraph } from './jsonld'

// SEO locales are keyed by the URL segment and match the app's routing locales
// (PUBLIC_LOCALES). Swedish is the URL "/se" but advertises hreflang/lang "sv" —
// that pairing is intentional (see htmlLangByLocale.se / localeMap.se / the sv-SE
// alternate). no/fi were a legacy backlog and have been removed.
export const SEO_LOCALES = ['da', 'en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'se'] as const

export type SeoLocale = (typeof SEO_LOCALES)[number]

type PublicMetadata = {
  title: string
  description: string
  keywords: string
  ogTitle: string
  ogDescription: string
  ogImageAlt: string
  twitterTitle: string
  twitterDescription: string
}

// og:locale per URL locale. Swedish (/se) → sv_SE.
export const localeMap: Record<SeoLocale, string> = {
  da: 'da_DK',
  en: 'en_US',
  de: 'de_DE',
  es: 'es_ES',
  fr: 'fr_FR',
  it: 'it_IT',
  nl: 'nl_NL',
  pl: 'pl_PL',
  pt: 'pt_PT',
  se: 'sv_SE',
}

// <html lang> per URL locale. Matches the URL code except Swedish (/se → "sv").
export const htmlLangByLocale: Record<SeoLocale, string> = {
  da: 'da',
  en: 'en',
  de: 'de',
  es: 'es',
  fr: 'fr',
  it: 'it',
  nl: 'nl',
  pl: 'pl',
  pt: 'pt',
  se: 'sv',
}

// hreflang (regional) per URL locale — the single source for <link rel="alternate">
// across landing, blog and content pages. Swedish (/se) advertises "sv-SE".
export const hreflangByLocale: Record<SeoLocale, string> = {
  da: 'da-DK',
  en: 'en-US',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  se: 'sv-SE',
}

export const appLanguageByLocale: Record<SeoLocale, string> = {
  da: 'DA',
  en: 'EN',
  de: 'DE',
  es: 'ES',
  fr: 'FR',
  it: 'IT',
  nl: 'NL',
  pl: 'PL',
  pt: 'PT',
  se: 'SE',
}

export const metadataByLocale: Record<SeoLocale, PublicMetadata> = {
  da: {
    title: 'Prøv BookBuilderGo gratis i 7 dage · Skriv & udgiv din bog',
    description: 'Det komplette forfatter-studio. Skriv din bog med LITRA, design covers, og udgiv på Amazon, Apple Books & Google Play. Du beholder 100% af rettighederne. Start gratis i 7 dage.',
    keywords: 'AI bogskrivning, selvudgivelse, skriv bog med AI, LITRA, bogforfatter værktøj, Kindle udgivelse',
    ogTitle: 'BookBuilderGo — Skriv din bog med AI',
    ogDescription: 'Fra idé til udgivet bog på rekordtid med LITRA.',
    ogImageAlt: 'BookBuilderGo — AI bogskrivning',
    twitterTitle: 'BookBuilderGo — Skriv din bog med AI',
    twitterDescription: 'Fra idé til udgivet bog på rekordtid med LITRA.',
  },
  en: {
    title: 'Try BookBuilderGo Free for 7 Days · Write & Publish Your Book',
    description: 'The complete author studio. Write your book with LITRA, design covers, and publish on Amazon, Apple Books & Google Play. You keep 100% of your rights. Start free for 7 days.',
    keywords: 'AI book writing, self-publishing, write book with AI, LITRA, author tool, Kindle publishing',
    ogTitle: 'BookBuilderGo — Write your book with AI',
    ogDescription: 'From idea to published book in record time with LITRA.',
    ogImageAlt: 'BookBuilderGo — AI book writing',
    twitterTitle: 'BookBuilderGo — Write your book with AI',
    twitterDescription: 'From idea to published book in record time with LITRA.',
  },
  de: {
    title: 'BookBuilderGo 7 Tage kostenlos · Schreibe & veröffentliche dein Buch',
    description: 'Das komplette Autoren-Studio. Schreibe dein Buch mit LITRA, gestalte Cover und veröffentliche auf Amazon, Apple Books & Google Play. Du behältst 100% der Rechte. Starte 7 Tage kostenlos.',
    keywords: 'KI Buch schreiben, Self-Publishing, Buch mit KI schreiben, LITRA, Autorentool, Kindle Veröffentlichung',
    ogTitle: 'BookBuilderGo — Schreibe dein Buch mit KI',
    ogDescription: 'Von der Idee zum veröffentlichten Buch in Rekordzeit mit LITRA.',
    ogImageAlt: 'BookBuilderGo — KI-Buchschreiben',
    twitterTitle: 'BookBuilderGo — Schreibe dein Buch mit KI',
    twitterDescription: 'Von der Idee zum veröffentlichten Buch in Rekordzeit mit LITRA.',
  },
  es: {
    title: 'Prueba BookBuilderGo Gratis 7 Días · Escribe y publica tu libro',
    description: 'El estudio completo para autores. Escribe tu libro con LITRA, diseña portadas y publica en Amazon, Apple Books y Google Play. Conservas el 100% de los derechos. Empieza gratis 7 días.',
    keywords: 'escritura de libros IA, autopublicación, escribir libro con IA, LITRA, herramienta para autores, publicación Kindle',
    ogTitle: 'BookBuilderGo — Escribe tu libro con IA',
    ogDescription: 'De la idea al libro publicado en tiempo récord con LITRA.',
    ogImageAlt: 'BookBuilderGo — escritura de libros con IA',
    twitterTitle: 'BookBuilderGo — Escribe tu libro con IA',
    twitterDescription: 'De la idea al libro publicado en tiempo récord con LITRA.',
  },
  fr: {
    title: 'Essayez BookBuilderGo Gratuitement 7 Jours · Écrivez et publiez votre livre',
    description: 'Le studio d’auteur complet. Écrivez votre livre avec LITRA, créez des couvertures et publiez sur Amazon, Apple Books et Google Play. Vous gardez 100% des droits. Commencez gratuitement pendant 7 jours.',
    keywords: 'écriture de livre IA, autoédition, écrire un livre avec IA, LITRA, outil auteur, publication Kindle',
    ogTitle: 'BookBuilderGo — Écrivez votre livre avec l’IA',
    ogDescription: 'De l’idée au livre publié en un temps record avec LITRA.',
    ogImageAlt: 'BookBuilderGo — écriture de livres par IA',
    twitterTitle: 'BookBuilderGo — Écrivez votre livre avec l’IA',
    twitterDescription: 'De l’idée au livre publié en un temps record avec LITRA.',
  },
  it: {
    title: 'Prova BookBuilderGo Gratis 7 Giorni · Scrivi e pubblica il tuo libro',
    description: 'Lo studio completo per autori. Scrivi il tuo libro con LITRA, progetta le copertine e pubblica su Amazon, Apple Books e Google Play. Mantieni il 100% dei diritti. Inizia gratis per 7 giorni.',
    keywords: 'scrittura di libri con IA, autopubblicazione, scrivere un libro con IA, LITRA, strumento per autori, pubblicazione Kindle',
    ogTitle: 'BookBuilderGo — Scrivi il tuo libro con l’IA',
    ogDescription: 'Dall’idea al libro pubblicato in tempo record con LITRA.',
    ogImageAlt: 'BookBuilderGo — scrittura di libri con IA',
    twitterTitle: 'BookBuilderGo — Scrivi il tuo libro con l’IA',
    twitterDescription: 'Dall’idea al libro pubblicato in tempo record con LITRA.',
  },
  nl: {
    title: 'Probeer BookBuilderGo 7 Dagen Gratis · Schrijf en publiceer je boek',
    description: 'De complete auteursstudio. Schrijf je boek met LITRA, ontwerp omslagen en publiceer op Amazon, Apple Books en Google Play. Je behoudt 100% van de rechten. Begin 7 dagen gratis.',
    keywords: 'AI boek schrijven, self-publishing, boek schrijven met AI, LITRA, auteurstool, Kindle publicatie',
    ogTitle: 'BookBuilderGo — Schrijf je boek met AI',
    ogDescription: 'Van idee naar gepubliceerd boek in recordtijd met LITRA.',
    ogImageAlt: 'BookBuilderGo — AI boek schrijven',
    twitterTitle: 'BookBuilderGo — Schrijf je boek met AI',
    twitterDescription: 'Van idee naar gepubliceerd boek in recordtijd met LITRA.',
  },
  pl: {
    title: 'Wypróbuj BookBuilderGo Za Darmo 7 Dni · Napisz i wydaj swoją książkę',
    description: 'Kompletne studio autorskie. Napisz książkę z LITRA, projektuj okładki i publikuj na Amazon, Apple Books i Google Play. Zachowujesz 100% praw. Zacznij za darmo na 7 dni.',
    keywords: 'pisanie książek AI, self-publishing, napisz książkę z AI, LITRA, narzędzie dla autorów, publikacja Kindle',
    ogTitle: 'BookBuilderGo — Napisz książkę z AI',
    ogDescription: 'Od pomysłu do opublikowanej książki w rekordowym czasie z LITRA.',
    ogImageAlt: 'BookBuilderGo — pisanie książek z AI',
    twitterTitle: 'BookBuilderGo — Napisz książkę z AI',
    twitterDescription: 'Od pomysłu do opublikowanej książki w rekordowym czasie z LITRA.',
  },
  pt: {
    title: 'Experimenta o BookBuilderGo Grátis 7 Dias · Escreve e publica o teu livro',
    description: 'O estúdio completo para autores. Escreve o teu livro com a LITRA, cria capas e publica na Amazon, Apple Books e Google Play. Manténs 100% dos direitos. Começa grátis durante 7 dias.',
    keywords: 'escrita de livros com IA, autopublicação, escrever livro com IA, LITRA, ferramenta para autores, publicação Kindle',
    ogTitle: 'BookBuilderGo — Escreve o teu livro com IA',
    ogDescription: 'Da ideia ao livro publicado em tempo recorde com a LITRA.',
    ogImageAlt: 'BookBuilderGo — escrita de livros com IA',
    twitterTitle: 'BookBuilderGo — Escreve o teu livro com IA',
    twitterDescription: 'Da ideia ao livro publicado em tempo recorde com a LITRA.',
  },
  se: {
    title: 'Prova BookBuilderGo Gratis i 7 Dagar · Skriv & publicera din bok',
    description: 'Den kompletta författarstudion. Skriv din bok med LITRA, designa omslag och publicera på Amazon, Apple Books & Google Play. Du behåller 100% av rättigheterna. Börja gratis i 7 dagar.',
    keywords: 'AI bokskrivning, egenutgivning, skriv bok med AI, LITRA, författarverktyg, Kindle publicering',
    ogTitle: 'BookBuilderGo — Skriv din bok med AI',
    ogDescription: 'Från idé till publicerad bok på rekordtid med LITRA.',
    ogImageAlt: 'BookBuilderGo — AI-bokskrivning',
    twitterTitle: 'BookBuilderGo — Skriv din bok med AI',
    twitterDescription: 'Från idé till publicerad bok på rekordtid med LITRA.',
  },
}

// hreflang alternates emitted on every landing page. Swedish: sv-SE → /se
// (intentional URL/hreflang pairing). x-default points at /en.
export const alternateLanguages: Record<string, string> = {
  'da-DK': 'https://www.bookbuildergo.com/da',
  'en-US': 'https://www.bookbuildergo.com/en',
  'de-DE': 'https://www.bookbuildergo.com/de',
  'es-ES': 'https://www.bookbuildergo.com/es',
  'fr-FR': 'https://www.bookbuildergo.com/fr',
  'it-IT': 'https://www.bookbuildergo.com/it',
  'nl-NL': 'https://www.bookbuildergo.com/nl',
  'pl-PL': 'https://www.bookbuildergo.com/pl',
  'pt-PT': 'https://www.bookbuildergo.com/pt',
  'sv-SE': 'https://www.bookbuildergo.com/se',
  'x-default': 'https://www.bookbuildergo.com/en',
}

export function localeFromPathname(pathname: string): SeoLocale | null {
  const segment = pathname.split('/').filter(Boolean)[0]?.toLowerCase()
  return SEO_LOCALES.includes(segment as SeoLocale) ? (segment as SeoLocale) : null
}

// JSON-LD structured data, gated by VITE_FF_JSONLD_SCHEMA. Default OFF → no
// script is injected (bit-identical with today's landing head).
const JSONLD_SCHEMA_ON =
  String(
    (import.meta.env as Record<string, string | undefined>).VITE_FF_JSONLD_SCHEMA ?? 'false',
  ).toLowerCase() === 'true'

// Create-or-update a single <script type="application/ld+json"> by id, mirroring
// the canonical/hreflang upsert pattern so the prerender snapshot captures it.
function upsertJsonLd(id: string, data: unknown): void {
  let element = document.head.querySelector<HTMLScriptElement>(`script#${id}`)
  if (!element) {
    element = document.createElement('script')
    element.id = id
    element.type = 'application/ld+json'
    document.head.appendChild(element)
  }
  element.textContent = JSON.stringify(data)
}

function upsertMeta(selector: string, attrs: Record<string, string>): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value)
}

function upsertLink(selector: string, attrs: Record<string, string>): void {
  let element = document.head.querySelector<HTMLLinkElement>(selector)
  if (!element) {
    element = document.createElement('link')
    document.head.appendChild(element)
  }
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value)
}

export function applyPublicSeoMetadata(locale: SeoLocale): void {
  const copy = metadataByLocale[locale] || metadataByLocale.en
  const canonical = `https://www.bookbuildergo.com/${locale}`
  const keywords = copy.keywords.split(',').map((keyword) => keyword.trim()).join(', ')

  document.documentElement.lang = htmlLangByLocale[locale] || locale
  document.title = copy.title
  upsertMeta('meta[name="description"]', { name: 'description', content: copy.description })
  upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords })
  upsertMeta('meta[name="author"]', { name: 'author', content: 'BookBuilderGo' })
  upsertMeta('meta[name="creator"]', { name: 'creator', content: 'BookBuilderGo' })
  upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' })

  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: localeMap[locale] || 'en_US' })
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical })
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'BookBuilderGo' })
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: copy.ogTitle })
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: copy.ogDescription })
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: 'https://www.bookbuildergo.com/og-image.jpg' })
  upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' })
  upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' })
  upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: copy.ogImageAlt })

  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: copy.twitterTitle })
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: copy.twitterDescription })
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: 'https://www.bookbuildergo.com/og-image.jpg' })

  upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical })
  Object.entries(alternateLanguages).forEach(([hrefLang, href]) => {
    upsertLink(`link[rel="alternate"][hreflang="${hrefLang}"]`, { rel: 'alternate', hrefLang, href })
  })

  // JSON-LD: Organization + WebSite + SoftwareApplication. Injected alongside the
  // canonical link so the prerender snapshot (which waits on canonical) captures it.
  if (JSONLD_SCHEMA_ON) {
    upsertJsonLd('landing-jsonld-schema', buildLandingGraph(locale))
  }
}
